import React, { useEffect, useState } from "react";
import { getDashboardData } from "../services/apiService";
import { useQuote } from "../context/QuoteContext";

const HR_NOTIFICATION_STORAGE_KEY = "fumind_hr_notifications";
const HR_NOTIFICATION_EVENT = "fumind-notifications-updated";
function loadHrNotifications() {
  if (typeof window === "undefined") return [];
  try {
    const stored = JSON.parse(window.localStorage.getItem(HR_NOTIFICATION_STORAGE_KEY) || "[]");
    return Array.isArray(stored) ? stored : [];
  } catch {
    return [];
  }
}
function formatNotificationDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

function Home() {
  const [news, setNews] = useState([]);
  const [hrNotifications, setHrNotifications] = useState(() => loadHrNotifications());
  const { setQuote } = useQuote();

  useEffect(() => {
    const refreshNotifications = () => setHrNotifications(loadHrNotifications());
    refreshNotifications();
    window.addEventListener(HR_NOTIFICATION_EVENT, refreshNotifications);
    window.addEventListener("storage", refreshNotifications);
    return () => {
      window.removeEventListener(HR_NOTIFICATION_EVENT, refreshNotifications);
      window.removeEventListener("storage", refreshNotifications);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        const data = await getDashboardData();

        // ✅ Update header quote ticker (AppLayout passes quote to Header)
        if (isMounted) {
          setQuote(data?.quote || "Welcome to FuMind.GI 🚀");
        }

        // ✅ Parse news from API response
        let newsData = data?.news;

        // 1) Convert string JSON -> object
        if (typeof newsData === "string") {
          try {
            newsData = JSON.parse(newsData);
          } catch (e) {
            console.error("JSON parse error:", e);
            newsData = [];
          }
        }

        // 2) Handle nested formats
        if (newsData?.articles) newsData = newsData.articles;
        if (newsData?.value) newsData = newsData.value;

        // 3) Ensure array
        if (!Array.isArray(newsData)) newsData = [];

        if (isMounted) {
          setNews(newsData);
        }
      } catch (error) {
        console.error("Dashboard Load Error:", error);

        if (isMounted) {
          setNews([]);
          setQuote("Welcome to FuMind.GI 🚀");
        }
      }
    };

    loadData();

    // ✅ Keep your refresh interval (same as earlier approach)
    const interval = setInterval(loadData, 6000000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [setQuote]);

  return (
    <div style={contentLayout}>
      {/* ✅ LEFT: NEWS */}
      <div style={leftColumn}>
        <div style={cardStyle}>
          <h3>AI News</h3>

          {news.length === 0 && <p>No news available</p>}

          {news.map((item, index) => (
            <div key={index} style={{ marginBottom: 15 }}>
              {item?.image && (
                <img src={item.image} alt="news" style={imageStyle} />
              )}

              <h4
                style={titleStyle}
                onClick={() => item?.url && window.open(item.url, "_blank")}
              >
                {item?.title || "Untitled"}
              </h4>

              <p>{item?.summary || item?.description || ""}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ✅ RIGHT PANEL */}
      <div style={rightColumn}>
        <div style={cardStyle}>
          <h4>HR Notifications</h4>
          {hrNotifications.length === 0 && <p>No HR notifications yet.</p>}
          {hrNotifications.slice(0, 8).map((item) => (
            <div key={item.id} style={notificationItemStyle}>
              <strong>{item.title || "HR notification"}</strong>
              <p style={notificationMessageStyle}>{item.message || ""}</p>
              <div style={notificationDateStyle}>{formatNotificationDate(item.createdOn)}</div>
            </div>
          ))}
        </div>
        <div style={cardStyle}>
          <h4>Vision</h4>
          <p>To empower every factory with AI operations.</p>

          <h4>Mission</h4>
          <p>To unify data and democratize AI.</p>

          <h4>Core Objectives</h4>
          <ul>
            <li>Real-time insights</li>
            <li>Smart scheduling</li>
            <li>Predict failures</li>
            <li>Reduce defects</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

export default Home;

/* ✅ Inline styles (same pattern as your original Home.js approach) */
const contentLayout = { display: "flex", gap: 20 };
const leftColumn = { width: "65%" };
const rightColumn = { width: "35%" };

const cardStyle = {
  background: "#fff",
  padding: 20,
  borderRadius: 10,
  marginBottom: 15,
  boxShadow: "0px 2px 6px rgba(0,0,0,0.05)",
};

const imageStyle = {
  width: "100%",
  height: 220,
  objectFit: "cover",
  borderRadius: 8,
  marginBottom: 10,
};

const titleStyle = { cursor: "pointer", color: "#2563EB" };

const notificationItemStyle = {
  border: "1px solid #E5E7EB",
  borderRadius: 8,
  padding: 10,
  marginBottom: 10,
  background: "#F8FAFC",
};
const notificationMessageStyle = {
  margin: "6px 0",
  color: "#475569",
  fontSize: 13,
};
const notificationDateStyle = {
  color: "#64748B",
  fontSize: 12,
};
